import { useEffect, useRef, RefObject } from 'react';
import { gsap } from 'gsap';
import SplitText from 'gsap/SplitText';

import styles from './Hero.module.scss';
import { animateHeadlineIn } from './animateHeadlineIn';

// ----------------------------------------
// Carousel controller
// Owns: slide DOM discovery, dots, autoplay timer/progress ring,
// hover-pause, pause/play button, swipe/drag, transition animations.
// ----------------------------------------

// -------------------- Tunable constants

const AUTO_TIMER_DUR       = 7000;          // ms between auto-advances
const TIMER_RING_DASH      = 69.12;         // SVG strokeDasharray for the ring

const BG_FADE_DURATION     = 0.8;           // Background cross-fade
const SLIDE_EXIT_DURATION  = 0.55;          // Outgoing content exit
const SLIDE_EXIT_EASE      = 'power2.in';
const SLIDE_ENTER_DURATION = 1;             // Incoming content base duration
const FA_DISTANCE          = '2rem';        // Initial y-offset for entering / exiting content

const SWIPE_THRESHOLD      = 50;            // px horizontal travel to register a swipe
const SWIPE_RATIO          = 1.2;           // |dx| must beat |dy| by this factor

// -------------------- Types

type UseHeroCarouselArgs = {
  enabled: boolean;
  isAnimInComplete: boolean;
  enableHoverPause: boolean;
  viewportRef: RefObject<HTMLDivElement>;
  dotsContainerRef: RefObject<HTMLDivElement>;
  timerCircleRef: RefObject<SVGCircleElement>;
  pauseButtonRef: RefObject<HTMLButtonElement>;
  prevButtonRef: RefObject<HTMLButtonElement>;
  nextButtonRef: RefObject<HTMLButtonElement>;
  slideContentRefs: RefObject<(HTMLDivElement | null)[]>;
  rootElRef: RefObject<HTMLDivElement>;
  log: (...args: any[]) => void;
  // Fires the moment a transition begins (use to hide outgoing per-slide UI like the CursorFollower).
  onSlideExit?: (prevIndex: number, nextIndex: number) => void;
  // Fires when the incoming slide's content begins entering (use to show new per-slide UI).
  onSlideEnter?: (nextIndex: number) => void;
  // Fires when the full transition timeline completes (outgoing slide is invisible and settled).
  onTransitionComplete?: (prevIndex: number, nextIndex: number) => void;
};

// -------------------- Hook

export function useHeroCarousel({
  enabled,
  isAnimInComplete,
  enableHoverPause,
  viewportRef,
  dotsContainerRef,
  timerCircleRef,
  pauseButtonRef,
  prevButtonRef,
  nextButtonRef,
  slideContentRefs,
  rootElRef,
  log,
  onSlideExit,
  onSlideEnter,
  onTransitionComplete,
}: UseHeroCarouselArgs) {

  // Stable refs to callbacks so the main effect doesn't have to re-run when they change.
  const onSlideExitRef = useRef(onSlideExit);
  const onSlideEnterRef = useRef(onSlideEnter);
  const onTransitionCompleteRef = useRef(onTransitionComplete);
  onSlideExitRef.current = onSlideExit;
  onSlideEnterRef.current = onSlideEnter;
  onTransitionCompleteRef.current = onTransitionComplete;

  // Bridge between the carousel effect and the anim-in completion effect.
  // The effect populates this with a start function; the second effect calls it.
  const startCarouselTimerRef = useRef<(() => void) | null>(null);

  // -------------------- Main carousel setup

  useEffect(() => {
    if (!enabled || !viewportRef.current) return;

    const viewportNode = viewportRef.current;
    const slideElements = Array.from(
      viewportNode.querySelectorAll<HTMLElement>(`.${styles['carousel-slide']}`)
    );
    const slideCount = slideElements.length;

    log(`Carousel init: ${slideCount} slides, ${AUTO_TIMER_DUR / 1000}s autoplay`);

    // -------------------- Mutable state (closure-scoped to avoid re-renders)

    let currentIndex = 0;
    let isPaused = false;
    let isHovered = false;
    let isTransitioning = false; // True while a slide enter animation is in progress
    let timerRunning = false;    // True while an autoplay rAF is currently scheduled
    let hasStarted = false;      // True once the carousel has been "handed off" to autoplay at least once
    let wasRunningBeforeHide = false; // Tracks timer state across tab visibility changes
    let animFrame = 0;
    let timerAdjustedStart = 0;
    let pausedElapsed = 0;
    let activeSlideTimeline: gsap.core.Timeline | null = null;

    // Per-slide SplitText instances tracked for cleanup
    const slideSplits: (SplitText | null)[] = slideElements.map((): SplitText | null => null);

    // -------------------- Timer ring radius (parsed once)

    const timerCirc = timerCircleRef.current
      ? parseFloat(timerCircleRef.current.getAttribute('stroke-dasharray') || `${TIMER_RING_DASH}`)
      : TIMER_RING_DASH;

    // -------------------- Forward declarations (mutual recursion)

    let goTo: (target: number) => void;
    let scrollNext: () => void;

    // -------------------- Timer animation (drives the progress ring)

    const animateTimer = (timestamp: number) => {
      const elapsed = timestamp - timerAdjustedStart;
      const progress = Math.min(elapsed / AUTO_TIMER_DUR, 1);
      const offset = timerCirc * (1 - progress);
      if (timerCircleRef.current) {
        timerCircleRef.current.style.strokeDashoffset = offset.toString();
      }
      if (progress >= 1) {
        timerRunning = false;
        scrollNext();
        return;
      }
      animFrame = requestAnimationFrame(animateTimer);
    };

    // -------------------- Timer control helpers
    // Centralized start/cancel so every code path that touches the autoplay
    // timer goes through a single pair of functions. This is the foundation
    // of "airtight": no path can leak an rAF or stack a duplicate one.

    const cancelTimer = () => {
      cancelAnimationFrame(animFrame);
      timerRunning = false;
    };

    const startTimer = () => {
      cancelAnimationFrame(animFrame);
      timerAdjustedStart = performance.now() - pausedElapsed;
      animFrame = requestAnimationFrame(animateTimer);
      timerRunning = true;
    };

    // -------------------- Dots (active-state visuals)

    const updateDots = () => {
      const dotEls = dotsContainerRef.current
        ? (Array.from(dotsContainerRef.current.children) as HTMLElement[])
        : [];
      dotEls.forEach((dot, i) => {
        dot.dataset.active = i === currentIndex ? 'true' : 'false';
      });
    };

    // -------------------- Slide accessibility state (aria + pointer-events)

    const updateSlideA11y = () => {
      slideElements.forEach((slide, i) => {
        slide.setAttribute('aria-hidden', i !== currentIndex ? 'true' : 'false');
        slide.style.pointerEvents = i !== currentIndex ? 'none' : '';
      });
    };

    // -------------------- Pause/play button icon state

    const updateIconState = () => {
      pauseButtonRef.current?.setAttribute('data-paused', isPaused ? 'true' : 'false');
    };

    // -------------------- Create dots (one-time)

    const dotsContainer = dotsContainerRef.current;
    if (dotsContainer && slideCount > 1) {
      dotsContainer.innerHTML = '';
      slideElements.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
        dot.dataset.carouselDot = 'true';
        dot.innerHTML = '<span></span>';
        dot.addEventListener('click', () => {
          log(`Dot clicked: slide ${index + 1}`);
          goTo(index);
        });
        dotsContainer.appendChild(dot);
      });
      log(`Created ${slideCount} dots`);
    }

    // -------------------- Per-slide animate-in (returns paused timeline)

    const createSlideInTimeline = (contentEl: HTMLElement, slideIndex: number) => {
      // Reset container state left over from any previous exit
      gsap.set(contentEl, { y: 0, opacity: 1 });

      const gDur = SLIDE_ENTER_DURATION;
      // Built un-paused — this timeline is nested into the master in runTransition
      // and would otherwise never advance (GSAP respects the child's paused state).
      const tl = gsap.timeline().add('start');

      // Headline (uses shared helper)
      const wrap = contentEl.querySelector<HTMLElement>(`.${styles['headline-wrap']}`);
      const head = wrap?.querySelector<HTMLElement>('h1');
      if (wrap && head) {
        if (slideSplits[slideIndex]) {
          slideSplits[slideIndex]!.revert();
          slideSplits[slideIndex] = null;
        }
        const { split, isSingleLine } = animateHeadlineIn({
          parentTl: tl,
          position: 'start',
          wrapEl: wrap,
          headlineEl: head,
          gDur,
          singleLineMode: 'chars',
        });
        slideSplits[slideIndex] = split;
        // Center the wrap now, after SplitText has already measured the line count.
        // The chars are inline-block — without this they animate in left-aligned.
        // Setting text-align: center in CSS would break SplitText's line detection,
        // so it must be applied via JS after the split. On first visit the wrap has
        // no textAlign; on subsequent visits the tl.call below already set it.
        if (isSingleLine) wrap.style.textAlign = 'center';
        tl.call(() => {
          if (slideSplits[slideIndex]) {
            slideSplits[slideIndex]!.revert();
            slideSplits[slideIndex] = null;
          }
          wrap.style.textAlign = 'center';
        });
      }

      // Pill
      const pillWrap = contentEl.querySelector<HTMLElement>(`.${styles['pill-wrap']}`);
      if (pillWrap) {
        gsap.killTweensOf(pillWrap);
        tl.fromTo(pillWrap,
          { y: '3rem', opacity: 0 },
          { y: 0, opacity: 1, duration: 0.4, ease: 'power3.out' },
          'start+=0.4'
        );
      }

      // Body
      const bodyWrap = contentEl.querySelector<HTMLElement>(`.${styles['body-wrap']}`);
      if (bodyWrap) {
        gsap.killTweensOf(bodyWrap);
        tl.fromTo(bodyWrap,
          { rotate: 1, y: '1rem', opacity: 0 },
          { rotate: 0, y: 0, opacity: 1, duration: gDur * 0.4, ease: 'power1.out', transformOrigin: '25% center' },
          '-=0.3'
        );
      }

      // Logo
      const logoWrap = contentEl.querySelector<HTMLElement>(`.${styles['logo-wrap']}`);
      if (logoWrap) {
        gsap.killTweensOf(logoWrap);
        tl.fromTo(logoWrap,
          { y: '1.5rem', opacity: 0, scaleY: 0.9 },
          { y: 0, opacity: 1, scaleY: 1, transformOrigin: '50% 100%', ease: 'power3.out', duration: gDur / 1.75 },
          '-=0.3'
        );
      }

      return tl;
    };

    // -------------------- Master transition (single timeline owns everything)

    const runTransition = (prevIndex: number, selectedIndex: number) => {
      log(`runTransition: ${prevIndex + 1} -> ${selectedIndex + 1}`);

      // Mark the transition active and park the timer at full (no progress)
      // BEFORE building the new timeline, so anything observing state during
      // construction sees a consistent "transitioning" snapshot.
      isTransitioning = true;
      cancelTimer();
      pausedElapsed = 0;
      if (timerCircleRef.current) {
        timerCircleRef.current.style.strokeDashoffset = timerCirc.toString();
      }

      // Kill any in-progress transition timeline before building a new one
      activeSlideTimeline?.kill();
      activeSlideTimeline = null;

      const prevBg = slideElements[prevIndex]?.querySelector<HTMLElement>(`.${styles['media-bg']}`);
      const nextBg = slideElements[selectedIndex]?.querySelector<HTMLElement>(`.${styles['media-bg']}`);
      const prevContent = slideContentRefs.current?.[prevIndex];
      const nextContent = slideContentRefs.current?.[selectedIndex];

      // Revert outgoing split immediately so it doesn't interfere with exit tween
      if (slideSplits[prevIndex]) {
        slideSplits[prevIndex]!.revert();
        slideSplits[prevIndex] = null;
      }

      // Pre-position the entering content so it's invisible until its turn
      if (nextContent) gsap.set(nextContent, { opacity: 0 });

      // Build one timeline that owns bg cross-fade, content exit, and content enter
      const tl = gsap.timeline();

      // Notify: transition has begun (hide outgoing per-slide UI)
      onSlideExitRef.current?.(prevIndex, selectedIndex);

      if (prevBg)     tl.to(prevBg,    { opacity: 0, duration: BG_FADE_DURATION,  ease: 'power1.inOut' }, 0);
      if (nextBg)     tl.fromTo(nextBg, { opacity: 0 }, { opacity: 1, duration: BG_FADE_DURATION, ease: 'power1.inOut' }, 0);
      if (prevContent) {
        gsap.killTweensOf(prevContent);
        tl.to(prevContent, { y: `-${FA_DISTANCE}`, opacity: 0, duration: SLIDE_EXIT_DURATION, ease: SLIDE_EXIT_EASE }, 0);
      }
      if (nextContent) {
        tl.add(createSlideInTimeline(nextContent, selectedIndex), SLIDE_EXIT_DURATION);
      }
      // Notify: incoming content is entering (show new per-slide UI)
      tl.call(() => onSlideEnterRef.current?.(selectedIndex), undefined, SLIDE_EXIT_DURATION);

      // Start the auto-timer only after the slide-in animation finishes, so the
      // timer ring doesn't visibly progress during the transition.
      tl.eventCallback('onComplete', () => {
        isTransitioning = false;
        pausedElapsed = 0;
        hasStarted = true;
        if (!isPaused && !isHovered && !document.hidden) {
          startTimer();
        }
        // Notify: transition is fully settled (outgoing slide is now invisible).
        onTransitionCompleteRef.current?.(prevIndex, selectedIndex);
      });

      activeSlideTimeline = tl;

      updateDots();
      updateSlideA11y();
    };

    // -------------------- Controller API

    goTo = (target: number) => {
      const next = ((target % slideCount) + slideCount) % slideCount;
      if (next === currentIndex) return;
      if (isTransitioning) return;
      const previous = currentIndex;
      currentIndex = next;
      runTransition(previous, currentIndex);
    };
    scrollNext = () => goTo(currentIndex + 1);

    // -------------------- Swipe / drag detection

    let pointerStartX = 0;
    let pointerStartY = 0;
    let pointerActive = false;
    let pointerDidSwipe = false;
    let pointerId = -1;

    const onPointerDown = (e: PointerEvent) => {
      // Ignore secondary buttons (right-click, middle-click)
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      pointerActive = true;
      pointerDidSwipe = false;
      pointerStartX = e.clientX;
      pointerStartY = e.clientY;
      pointerId = e.pointerId;
      // Pointer capture is deferred until we've committed to a swipe so a clean
      // click still hits the underlying link and the CursorFollower doesn't
      // perceive a synthetic pointerleave on mousedown.
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!pointerActive || pointerDidSwipe) return;
      const dx = e.clientX - pointerStartX;
      const dy = e.clientY - pointerStartY;
      if (Math.abs(dx) >= SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * SWIPE_RATIO) {
        pointerDidSwipe = true;
        try { viewportNode.setPointerCapture?.(pointerId); } catch {}
        log(`Swipe detected: dx=${dx.toFixed(0)}`);
        if (dx < 0) scrollNext();
        else goTo(currentIndex - 1);
      }
    };
    const onPointerEnd = () => { pointerActive = false; };

    // Capture-phase click suppression: cancel link clicks that follow a swipe
    const onClickCapture = (e: MouseEvent) => {
      if (pointerDidSwipe) {
        e.stopPropagation();
        e.preventDefault();
        pointerDidSwipe = false;
      }
    };

    // Block native anchor-drag, which otherwise kills our pointermove stream
    const onDragStart = (e: DragEvent) => e.preventDefault();

    viewportNode.addEventListener('pointerdown', onPointerDown);
    viewportNode.addEventListener('pointermove', onPointerMove);
    viewportNode.addEventListener('pointerup', onPointerEnd);
    viewportNode.addEventListener('pointercancel', onPointerEnd);
    viewportNode.addEventListener('click', onClickCapture, true);
    viewportNode.addEventListener('dragstart', onDragStart);

    // -------------------- Hover pause/resume (pauses timer while over the hero)
    // Disabled when enableHoverPause is false.

    const heroEl = rootElRef.current;
    const onMouseEnter = () => {
      if (isPaused) return;
      isHovered = true;
      log('Carousel hover pause');
      // Only touch timer math when a timer is actually running. Prevents the
      // "hover before timer ever started" bug from corrupting timerAdjustedStart.
      if (!timerRunning) return;
      pausedElapsed = performance.now() - timerAdjustedStart;
      cancelTimer();
    };
    const onMouseLeave = () => {
      isHovered = false;
      if (isPaused || isTransitioning || document.hidden) return;
      // Only act once the carousel has officially started (avoids spuriously
      // starting a timer before initial anim-in is complete).
      if (!hasStarted || timerRunning) return;
      // Resume from stored progress, or start a fresh cycle if the user was
      // hovering through a transition and only released after it completed
      // (pausedElapsed will be 0 in that case → startTimer begins fresh).
      log(pausedElapsed > 0 ? 'Carousel hover resume' : 'Carousel hover start (post-transition)');
      startTimer();
    };
    if (enableHoverPause) {
      heroEl?.addEventListener('mouseenter', onMouseEnter);
      heroEl?.addEventListener('mouseleave', onMouseLeave);
    }

    // -------------------- Pause / play button

    const pauseButton = pauseButtonRef.current;
    const onPauseClick = () => {
      isPaused = !isPaused;
      log(`Carousel ${isPaused ? 'paused' : 'resumed'}`);
      if (isPaused) {
        // Capture progress only if a timer is currently running.
        if (timerRunning) {
          pausedElapsed = performance.now() - timerAdjustedStart;
        }
        cancelTimer();
      } else if (!isHovered && !isTransitioning && !document.hidden) {
        startTimer();
      }
      updateIconState();
    };
    pauseButton?.addEventListener('click', onPauseClick);

    // -------------------- Prev / Next arrow buttons

    const onPrevClick = () => goTo(currentIndex - 1);
    const onNextClick = () => goTo(currentIndex + 1);
    prevButtonRef.current?.addEventListener('click', onPrevClick);
    nextButtonRef.current?.addEventListener('click', onNextClick);

    // -------------------- Tab visibility (pause/resume when the tab is hidden)
    // Without this, rAF stops while hidden then fires once on return with a
    // huge elapsed delta — instantly tripping progress >= 1 and snapping forward.

    const onVisibilityChange = () => {
      if (document.hidden) {
        wasRunningBeforeHide = timerRunning;
        if (timerRunning) {
          pausedElapsed = performance.now() - timerAdjustedStart;
          cancelTimer();
        }
      } else {
        if (wasRunningBeforeHide && !isPaused && !isHovered && !isTransitioning) {
          startTimer();
        }
        wasRunningBeforeHide = false;
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // -------------------- Initial state
    // Non-first slides: hide bg + offset content, ready to animate in.

    log('Carousel ready');
    slideElements.forEach((el, i) => {
      if (i !== 0) {
        const bg = el.querySelector<HTMLElement>(`.${styles['media-bg']}`);
        if (bg) gsap.set(bg, { opacity: 0 });
        const content = slideContentRefs.current?.[i];
        if (content) gsap.set(content, { y: FA_DISTANCE, opacity: 0 });
      }
    });
    updateDots();
    updateSlideA11y();
    updateIconState();

    // Expose a start function so the anim-in completion handler can kick off the timer
    startCarouselTimerRef.current = () => {
      if (isPaused || isHovered || isTransitioning || document.hidden) {
        // Even if we can't start the rAF right now (e.g. user is hovering),
        // the carousel is considered "live" so hover-leave can pick it up.
        hasStarted = true;
        return;
      }
      log('Carousel timer starting (post anim-in)');
      pausedElapsed = 0;
      hasStarted = true;
      startTimer();
    };

    // -------------------- Cleanup

    return () => {
      log('Carousel destroyed');
      cancelTimer();
      activeSlideTimeline?.kill();
      activeSlideTimeline = null;
      startCarouselTimerRef.current = null;
      heroEl?.removeEventListener('mouseenter', onMouseEnter);
      heroEl?.removeEventListener('mouseleave', onMouseLeave);
      pauseButton?.removeEventListener('click', onPauseClick);
      prevButtonRef.current?.removeEventListener('click', onPrevClick);
      nextButtonRef.current?.removeEventListener('click', onNextClick);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      viewportNode.removeEventListener('pointerdown', onPointerDown);
      viewportNode.removeEventListener('pointermove', onPointerMove);
      viewportNode.removeEventListener('pointerup', onPointerEnd);
      viewportNode.removeEventListener('pointercancel', onPointerEnd);
      viewportNode.removeEventListener('click', onClickCapture, true);
      viewportNode.removeEventListener('dragstart', onDragStart);
      slideSplits.forEach((split) => split?.revert());
    };

  }, [enabled]);

  // -------------------- Start the autoplay timer once the page-load anim completes

  useEffect(() => {
    if (isAnimInComplete) startCarouselTimerRef.current?.();
  }, [isAnimInComplete]);
}
